import { UseFormRegisterReturn, useForm } from "react-hook-form";
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { GyminifyEmployee } from "../../../service/model/user.model";
import { Box, Button, Dialog, DialogTitle, Grid } from "@mui/material";
import FormField from "../../../components/form-controls/form-field";
import theme from "../../../app/app-theme";
import { PermissionCode } from "../../../shared/enums";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { selectLoggedInUser } from "../../auth/auth.slice";
import useRoles from "../../../hooks/use-roles";
import { getPermissionsForLoggedInUser, isAdmin } from "../../../shared/utils";
import { updateUser } from "../user.slice";
import FormCloseButton from "../../../components/form-controls/form-close-button";
import { FormTextField } from "../../../components/form-input-field-wrappers/form-controller-inputs/form-text-field";
import { FormSelectField } from "../../../components/form-input-field-wrappers/form-controller-inputs/form-select-field";

interface AdminUserFormProps {
    readonly user?: GyminifyEmployee
    readonly editable?: boolean
    readonly onClose: () => void
    // readonly onSubmit: (data: User) => void
}

const style = {
    // position: 'absolute' as 'absolute',
    // top: '50%',
    // left: '50%',
    // transform: 'translate(-50%, -50%)',
    // width: '100%',
    // bgcolor: 'background.paper',
    // border: '2px solid #000',
    // boxShadow: 24,
    p: 2,
};

const validationSchema = yup.object().shape({
    username: yup.string().required("Username is required"),
    roles: yup.string().required("Role is required"),
    firstName: yup.string().required("First name is required"),
    lastName: yup.string().required("Last name is required"),
    email: yup.string().email("Not a valid email"),
    // gender: yup.string().required("Gender is required"),
    dateOfBirth: yup.string(),
    contact: yup.string().required("Contact is required").matches(/^\+947\d{8}$/, 'Contact number follow format +947XXXXXXXX (e.g. +94771399297)').length(12),
    address: yup.string(),
});

export type FormValues = yup.InferType<typeof validationSchema>;

const AdminUserForm: React.FC<AdminUserFormProps> = ({ user, onClose }) => {
    const dispatch = useAppDispatch();
    const loggedInUser = useAppSelector(selectLoggedInUser);
    const permissions = getPermissionsForLoggedInUser(loggedInUser);
    const [roles] = useRoles();
    const { register, handleSubmit, formState: { errors } } = useForm<FormValues>({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            username: user?.user.username ?? '',
            firstName: user?.firstName ?? '',
            lastName: user?.lastName ?? '',
            email: user?.email ?? '',
            dateOfBirth: user?.dateOfBirth ?? '',
            contact: user?.contact,
            address: user?.address ?? ''
        }
    });

    const onSubmitHandler = (data: any) => {
        const selectedRoles = [roles.find(role => role.name === data.roles)];
        data = { ...data, roles: selectedRoles };

        const updatedUser = {
            ...user,
            ...data
        }
        loggedInUser && dispatch(updateUser({ ...updatedUser, id: user?.id })).then(resp => {
            if (resp.meta.requestStatus === 'fulfilled') {
                onClose();
            }
        });
    };

    const getRegister = (name: keyof FormValues, validation?: any): UseFormRegisterReturn => {
        return register(name, validation);
    };

    // const getRoleValue = (role: string) => {
    //     switch (role) {
    //         // case UserRoles.Admin:
    //         //     return "Admin";
    //         case UserRoles.Owner:
    //             return "Owner";
    //         case UserRoles.Staff:
    //             return "Staff";
    //         default:
    //             return "Invalid role";
    //     }
    // };

    return (
        // <Dialog open={true} onClose={onClose} sx={style} fullWidth maxWidth='md'>
        //     <DialogTitle sx={{ paddingBottom: 0 }}>User Details</DialogTitle>

        //     <Box sx={style}>
        //         <>
        //             <FormCloseButton onClose={onClose} />
                    <form onSubmit={handleSubmit(onSubmitHandler)}>
                        <Grid container direction="row" className="mt-0 my-0" justifyContent='space-between'>
                            <FormField>
                                <FormTextField
                                    id="username"
                                    label="Username"
                                    name="username"
                                    defaultValue={user?.user.username}
                                    disabled={true}
                                    errorMessage={errors.username?.message}
                                    register={register}
                                />
                            </FormField>
                            {(isAdmin(loggedInUser?.user) || permissions.includes(PermissionCode.ROLES_VIEW)) &&
                                <FormField>
                                    <FormSelectField
                                        id={"roles"}
                                        label="Role"
                                        name="roles"
                                        defaultValue={permissions.at(0)}
                                        errorMessage={errors.roles?.message}
                                        options={roles.map(item => { return { label: item?.displayName, value: item?.name } })}
                                        register={register}
                                    />
                                </FormField>
                            }
                            <FormField>
                                <FormTextField
                                    id="firstName"
                                    label="First Name"
                                    name="firstName"
                                    defaultValue={user?.firstName}
                                    errorMessage={errors.firstName?.message}
                                    register={register}
                                />
                            </FormField>
                            <FormField>
                                <FormTextField
                                    id="lastName"
                                    label="Last Name"
                                    name="lastName"
                                    defaultValue={user?.lastName}
                                    errorMessage={errors.lastName?.message}
                                    register={register}
                                />
                            </FormField>
                            <FormField>
                                <FormTextField
                                    id="email"
                                    label="Email"
                                    name="email"
                                    defaultValue={user?.email}
                                    errorMessage={errors.email?.message}
                                    register={register}
                                />
                            </FormField>
                            {/* <FormField>
                                <DropdownInput
                                    id={"gender"}
                                    label="Gender"
                                    defaultValue={user?.gender}
                                    error={errors.gender !== undefined}
                                    helperText={errors.gender?.message}
                                    options={["Male", "Female"].map(item => { return { label: item, value: item } })}
                                    register={register("gender")}
                                />
                            </FormField> */}
                            <FormField>
                                <FormTextField
                                    id="dateOfBirth"
                                    label="Birth Day"
                                    name="dateOfBirth"
                                    type="date"
                                    defaultValue={user?.dateOfBirth ?? ''}
                                    errorMessage={errors.dateOfBirth?.message}
                                    register={register}
                                />
                            </FormField>
                            <FormField>
                                <FormTextField
                                    id="contact"
                                    label="Contact"
                                    name="contact"
                                    defaultValue={user?.contact ?? "+94"}
                                    errorMessage={errors.contact?.message}
                                    register={register}
                                />
                            </FormField>
                            <FormField>
                                <FormTextField
                                    id="address"
                                    label="Address"
                                    name="address"
                                    defaultValue={user?.address}
                                    errorMessage={errors.address?.message}
                                    register={register}
                                />
                            </FormField>
                        </Grid>
                        <Grid container direction="row" justifyContent='end' sx={{ mt: 1 }}>
                            <Button type="button" variant="contained" sx={{ mr: 1, bgcolor: `${theme.palette.secondary.main}` }} onClick={onClose}>Cancel</Button>
                            <Button type="submit" variant="contained">Submit</Button>
                        </Grid>
                    </form>
        //         </>
        //     </Box>
        // </Dialog>
    );
}

export default AdminUserForm;